import { HouseIcon } from '@ghq-abi/design-system-icons';
import { useTranslate } from '@tolgee/react';
import { useCallback, useMemo } from 'react';
import { FiTool } from 'react-icons/fi';
import { useAbility } from '~/app/contexts/AbilityContext';
import { PagesPathsKeys, useFlags } from '~/app/contexts/FlagsContext';
import { RouteObject } from '~/app/types';
import { useGetEmployeeRole } from '~/entities/RoleSetting/api/useGetEmployeeRole';
import { useUser } from '~/shared/auth/useUser';
import { Book } from '~/shared/components/icons/Book';
import { Cog } from '~/shared/components/icons/Cog';
import { List } from '~/shared/components/icons/List';
import { Person } from '~/shared/components/icons/Person';
import { Phone } from '~/shared/components/icons/Phone';
import { isNonProductionEnv } from '~/shared/utils/environments';

type ExtendedRouteObject = RouteObject & { roles?: string[] };

export function useMenuRoutes() {
  const { t } = useTranslate();
  const ability = useAbility();
  const flags = useFlags();
  const user = useUser();
  const { data: employeeRole, isLoading: isLoadingEmployeeRole } =
    useGetEmployeeRole(user.employeeId || '');

  const hasAuthorizedFlags = useCallback(
    (authorizedFlags: PagesPathsKeys | PagesPathsKeys[]) => {
      if (
        typeof authorizedFlags === 'string' &&
        !flags[authorizedFlags].active
      ) {
        return false;
      }

      if (
        Array.isArray(authorizedFlags) &&
        !authorizedFlags.some(flag => flags[flag].active)
      ) {
        return false;
      }

      return true;
    },
    [flags]
  );

  const { menuRoutes, footerRoutes } = useMemo(() => {
    const menuRoutesList: ExtendedRouteObject[] = [];
    const footerRoutesList: RouteObject[] = [];

    if (hasAuthorizedFlags('home') && ability.can('view', 'Home')) {
      menuRoutesList.push({
        key: 'home',
        icon: <HouseIcon />,
        path: '/home',
        title: t('sideMenu.home'),
      });
    }

    if (
      hasAuthorizedFlags('my-requests') &&
      ability.can('view', 'MyRequests')
    ) {
      menuRoutesList.push(
        {
          key: 'Tickets',
          icon: <List />,
          path: '/tickets/my-tickets',
          title: t('sideMenu.myTickets'),
        },
        {
          key: 'Global Policies',
          icon: <Person />,
          path: 'https://anheuserbuschinbev.sharepoint.com/sites/ABIGlobalPolicies',
          title: t('sideMenu.globalPolicies'),
          external: true,
        },
        {
          key: 'Global Trainings',
          icon: <Book />,
          path: 'https://anheuserbuschinbev.sharepoint.com/sites/ABIGlobalTraining',
          title: t('sideMenu.globalTrainings'),
          external: true,
        },
        {
          key: 'Compliance Helpline',
          icon: <Phone />,
          path: 'https://app.convercent.com/en-us/LandingPage/8848861a-8794-ea11-a974-000d3ab9f062?_=1621361250018',
          title: t('sideMenu.complianceHelpline'),
          external: true,
        },
        {
          key: 'Role Settings',
          icon: <Cog />,
          path: '/settings',
          title: t('sideMenu.roleSettings'),
          roles: ['GlobalAdmin'],
        }
      );
    }

    if (isNonProductionEnv()) {
      menuRoutesList.push({
        key: 'dev-tools',
        icon: <FiTool />,
        path: '/dev-tools',
        title: '[DEV] Tools',
        roles: ['GlobalAdmin'],
      });
    }

    footerRoutesList.push({
      key: 'help',
      title: t('sideMenu.helpAndSupport'),
      routeComponents: [
        {
          key: 'help',
          title: t('sideMenu.isSomethingNotWorking'),
          path: 'https://abinbevww.service-now.com/abiex?id=sc_cat_item&sys_id=5ad2235adb1cc050f17eb29f29961934',
          external: true,
        },
        {
          key: 'request-access',
          title: t('sideMenu.requestAccess'),
          path: 'https://abinbevww.service-now.com/abiex?id=sc_cat_item&sys_id=168fe332c3f7e65013c9f5cf050131b6',
        },
      ],
    });

    const filteredMenuRoutes = menuRoutesList
      .filter(route => {
        if (route.roles) {
          if (isLoadingEmployeeRole) {
            return false;
          }
          if (employeeRole?.role) {
            return route.roles.includes(employeeRole.role);
          }
          return false;
        }
        return true;
      })
      .map(({ ...route }) => route);

    return { menuRoutes: filteredMenuRoutes, footerRoutes: footerRoutesList };
  }, [
    ability,
    hasAuthorizedFlags,
    t,
    employeeRole?.role,
    isLoadingEmployeeRole,
  ]);

  return { menuRoutes, footerRoutes, isLoading: isLoadingEmployeeRole };
}
